import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useAuth } from '../auth/AuthContext';
import { updateUiLocaleOnServer } from '../api/client';
import type { AppLocale } from './messages';
import { DEFAULT_LOCALE, MESSAGES, type MessageKey, isAppLocale } from './messages';
import { detectDeviceLocale } from './deviceLocale';
import { readSavedLocale, writeSavedLocale } from './localeStorage';
import { getI18nRuntimeLocale, setI18nRuntimeLocale } from './runtime';

type Ctx = {
  locale: AppLocale;
  setLocale: (l: AppLocale) => void;
  ready: boolean;
  t: (key: MessageKey) => string;
};

const LocaleContext = createContext<Ctx | null>(null);

function syncNativeNotificationChannelsLocale(): void {
  if (Platform.OS !== 'android') return;
  void import('../realtime/notifyIncomingMessage')
    .then((m) => m.syncNotificationChannelsLocale())
    .catch(() => {});
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { ready: authReady, user, accessToken, mergeUserLocale } = useAuth();
  // До чтения хранилища — тоже язык устройства: иначе первый кадр мелькал бы английским.
  const [locale, setLocaleState] = useState<AppLocale>(() => detectDeviceLocale() ?? DEFAULT_LOCALE);
  const [ready, setReady] = useState(false);
  const pushedLocalToServerRef = useRef(false);
  const userPickedLocaleRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = await readSavedLocale();
      // Без сохранённого выбора — язык устройства (аудит 2026-10-04, п. 32). Не записываем его:
      // это ещё не выбор человека, и смена языка телефона до первого выбора должна сработать.
      const next = isAppLocale(raw) ? raw : (detectDeviceLocale() ?? DEFAULT_LOCALE);
      if (!cancelled) {
        setI18nRuntimeLocale(next);
        setLocaleState(next);
        setReady(true);
        syncNativeNotificationChannelsLocale();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (user?.uiLocale && isAppLocale(user.uiLocale)) {
      const local = getI18nRuntimeLocale();
      if (accessToken && userPickedLocaleRef.current && local !== user.uiLocale) {
        return;
      }
      const v = user.uiLocale;
      setI18nRuntimeLocale(v);
      setLocaleState(v);
      void writeSavedLocale(v);
      syncNativeNotificationChannelsLocale();
    }
  }, [authReady, accessToken, user?.uiLocale, user?.userId]);

  useEffect(() => {
    if (!user?.userId) {
      pushedLocalToServerRef.current = false;
    }
  }, [user?.userId]);

  useEffect(() => {
    if (!authReady || !accessToken || !user?.userId) return;
    const local = getI18nRuntimeLocale();
    if (!isAppLocale(local)) return;
    const server = isAppLocale(user.uiLocale) ? user.uiLocale : null;
    if (server === local) {
      userPickedLocaleRef.current = false;
      return;
    }
    if (pushedLocalToServerRef.current) return;
    pushedLocalToServerRef.current = true;
    void updateUiLocaleOnServer(local)
      .then(() => {
        userPickedLocaleRef.current = false;
        return mergeUserLocale(local);
      })
      .catch(() => {
        pushedLocalToServerRef.current = false;
      });
  }, [authReady, accessToken, user?.userId, user?.uiLocale, mergeUserLocale]);

  const setLocale = useCallback(
    (l: AppLocale) => {
      userPickedLocaleRef.current = true;
      setI18nRuntimeLocale(l);
      setLocaleState(l);
      void writeSavedLocale(l);
      syncNativeNotificationChannelsLocale();
      if (accessToken) {
        void updateUiLocaleOnServer(l)
          .then(() => mergeUserLocale(l))
          .catch(() => {});
      }
    },
    [accessToken, mergeUserLocale],
  );

  const t = useCallback((key: MessageKey) => MESSAGES[locale][key], [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, ready, t }),
    [locale, setLocale, ready, t],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Ctx {
  const v = useContext(LocaleContext);
  if (!v) throw new Error('useLocale must be used within LocaleProvider');
  return v;
}
